import useGet from '@/core/composables/api/useGet'
import { computed, onMounted } from 'vue'
import { SurveyState } from '@/modules/survey/models/survey-state'
import type { Member } from '@/core/models/member/member'
import { useRoute, useRouter } from 'vue-router'
import usePut from '@/core/composables/api/usePut'
import { useI18n } from 'vue-i18n'
import { useSnackStore } from '@/stores/snack.store'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'
import useDelete from '@/core/composables/api/useDelete'
import type { QuestionGroup } from '@/modules/give-feedback/models/question-group'

export const useAssessment = () => {
  const route = useRoute()
  const router = useRouter()
  const { t } = useI18n()
  const snackStore = useSnackStore()

  const assessmentId = route.params.surveyTemplateId as string
  const { data, load, status, reload } = useGet<AssessmentDetail>(`assessment/${assessmentId}`)

  const finishUrl = computed(() => {
    if (!data.value) {
      return ''
    }

    return `assessment/${data.value.id}/finish`
  })

  const reopenhUrl = computed(() => {
    if (!data.value) {
      return ''
    }

    return `assessment/${data.value.id}/reopen`
  })

  const { mutate: mutateFinishAssesment, status: statusFinishAssessment } = usePut<
    string,
    AssessmentUpdateBody
  >(finishUrl)

  const { mutate: mutateReopenAssesment, status: statusReopenAssessment } = usePut<
    string,
    AssessmentUpdateBody
  >(reopenhUrl)

  const stopAssessment = async () => {
    if (!data.value) {
      return
    }

    await mutateFinishAssesment({ id: data.value.id, status: SurveyState.finished })

    switch (statusFinishAssessment.value) {
      case ApiRequestStatus.Success:
        await reload()
        snackStore.showInfo(t('survey.finish.success'))
        break
      case ApiRequestStatus.Error:
        snackStore.showError(t('survey.finish.error'))
        break
    }
  }

  const reopenAssessment = async () => {
    if (!data.value) {
      return
    }

    await mutateReopenAssesment({ id: data.value.id, status: SurveyState.running })

    switch (statusReopenAssessment.value) {
      case ApiRequestStatus.Success:
        await reload()
        snackStore.showInfo(t('survey.reopen.success'))
        break
      case ApiRequestStatus.Error:
        snackStore.showError(t('survey.reopen.error'))
        break
    }
  }

  const { remove: removeAssessment, status: statusDeleteAssessment } = useDelete<string>(
    `assessment/${assessmentId}`,
  )

  const deleteAssessment = async () => {
    await removeAssessment({})

    switch (statusDeleteAssessment.value) {
      case ApiRequestStatus.Success:
        router.push({ name: 'assessments' })
        snackStore.showInfo(t('survey.delete.success'))
        break
      case ApiRequestStatus.Error:
        snackStore.showError(t('survey.delete.error'))
        break
    }
  }

  onMounted(() => load())
  return {
    data,
    load,
    reload,
    status,
    stopAssessment,
    deleteAssessment,
    reopenAssessment,
  }
}

export interface AssessmentDetail {
  readonly id: string
  readonly title?: string
  readonly message?: JSON
  readonly feedbackTopic: {
    id: string
    title: string
    icon?: string
    questionGroups: ReadonlyArray<QuestionGroup>
  }
  readonly state: SurveyState
  readonly dueAt?: string
  readonly createdAt: string
  readonly finishedAt?: string
  readonly coachees: ReadonlyArray<AssessmentCoachee>
  readonly peers: ReadonlyArray<Member>
  readonly includeSelfFeedback: boolean
}

export interface AssessmentCoachee {
  readonly member: Member
  readonly selfFeedbackDone: boolean
  readonly finishedPeerIds: ReadonlyArray<string>
}

interface AssessmentUpdateBody {
  readonly id: string
  readonly status?: SurveyState
}
